import { PhoneOutlined } from '@ant-design/icons';
import { Alert, Form, Space, Tag, Typography, message } from 'antd';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { DEFAULT_SIP_CONFIG, type SipGlobalConfig, sipConfigStore } from '../../api/sip-config.js';
import { sipPhoneApi } from '../../api/sip-phone-api.js';
import { sipService } from '../../api/sip-service.js';
import { SipAssignmentCard } from './SipAssignmentCard.js';
import { SipGlobalConfigCard } from './SipGlobalConfigCard.js';

export function SipCallGlobalSettingsPage() {
  const cfg = useSyncExternalStore(
    sipConfigStore.subscribe,
    sipConfigStore.get,
    sipConfigStore.get,
  );
  const state = useSyncExternalStore(
    sipService.subscribe,
    sipService.getState,
    sipService.getState,
  );
  const [form] = Form.useForm<SipGlobalConfig>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    form.setFieldsValue(cfg);
  }, [cfg, form]);

  useEffect(() => {
    let cancelled = false;
    sipPhoneApi
      .getCallingConfig()
      .then((next) => {
        if (cancelled) return;
        sipConfigStore.set(next);
        form.setFieldsValue(next);
      })
      .catch(() => {
        message.error('加载超管通话参数失败');
      });
    return () => {
      cancelled = true;
    };
  }, [form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const saved = await sipPhoneApi.updateCallingConfig(values);
      sipConfigStore.set(saved);
      sipService.restart();
      message.success('超管通话参数已保存，所有用户将使用该 SIP 注册密码');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? '请检查表单');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    form.setFieldsValue(DEFAULT_SIP_CONFIG);
    sipConfigStore.reset();
    sipService.restart();
    message.success('已重置为默认通话配置');
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <PageTitle title="通话参数" />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="通话参数控制 SIP 网关连接和可分配号码范围"
        description="修改参数后，本机通话终端会重新注册；用户是否可用通话功能仍由号码分配页控制。"
      />
      <SipGlobalConfigCard
        cfg={cfg}
        form={form}
        saving={saving}
        onSave={handleSave}
        onReset={handleReset}
      />
    </div>
  );
}

export function SipCallAssignmentPage() {
  return (
    <div style={{ maxWidth: 980 }}>
      <PageTitle title="号码分配" />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="只有已分配号码且状态为启动的用户才会开通通话功能"
        description="选择用户管理中已存在的用户，并从全局号码范围内选择可用电话号码。"
      />
      <SipAssignmentCard />
    </div>
  );
}

export function SipDialerSettingsPage() {
  return <SipCallAssignmentPage />;
}

function PageTitle({ title }: { title: string }) {
  const state = sipService.getState();
  return (
    <Space style={{ marginBottom: 16 }} align="center">
      <PhoneOutlined style={{ fontSize: 20, color: '#0078d4' }} />
      <Typography.Title level={4} style={{ margin: 0 }}>
        {title}
      </Typography.Title>
      {state.registration === 'registered' ? (
        <Tag color="green">已注册</Tag>
      ) : state.registration === 'connecting' ? (
        <Tag color="blue">连接中</Tag>
      ) : state.registration === 'failed' ? (
        <Tag color="red">注册失败</Tag>
      ) : (
        <Tag>未启动</Tag>
      )}
    </Space>
  );
}
